import { BlankSlate } from "@msuite/ui/blank-slate";
import { ContentCard } from "@msuite/ui/content-card";
import { icon } from "@msuite/ui/icons";
import { useTranslation } from "@msuite/ui/localization";
import { useQuery } from "@msuite/ui/query";
import { responseHandler } from "@msuite/ui/utils";
import { UserIntegrationCard } from "@/components/cards/user-integration-card";
import { client } from "@/hono";
import { useCitadel } from "@/hooks/use-citadel";
import type { UserPanelBaseProps } from "../types";

export function UserIntegrations({ userId }: UserPanelBaseProps) {
	/** Hooks */
	const { t } = useTranslation();
	const { citadel } = useCitadel();

	/** Computed */
	// Which third-party systems an employee is wired into is an integrations
	// right, not part of the general tab a member reaches — the panel is absent
	// for a caller the integrations route would refuse, and it does not ask.
	const canReadIntegration = citadel?.can("read", "Integration") === true;

	/** Query */
	const { data } = useQuery({
		queryKey: ["users", userId, "integrations"],
		queryFn: async () => {
			const response = await client.users[":userId"].integrations.$get({
				param: { userId },
			});
			const data = await responseHandler(response);
			return data.integrations;
		},
		enabled: canReadIntegration,
	});

	/** Guard */
	if (!canReadIntegration) return null;

	return (
		<ContentCard
			icon={icon("Blocks")}
			title={t("integrations.integrations")}
		>
			<div className="v-stack">
				{!data?.length && <BlankSlate label={t("integrations.no_integrations_found")} />}
				{data?.map((integration) => (
					<UserIntegrationCard
						key={integration.integrationType}
						integrationType={integration.integrationType}
						userIntegration={integration.userIntegration}
					/>
				))}
			</div>
		</ContentCard>
	);
}
